import { Check, ChevronLeft, ChevronRight, Copy, Download, Eye, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { CalendarPost, CalendarResponse } from '@/types'
import { EmptyState, ErrorNote, LoadingScreen } from '@/components/ui'
import { api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'

function mondayOf(offset: number): Date {
  const now = new Date()
  const dow = (now.getDay() + 6) % 7 // Lun=0 … Dom=6
  const m = new Date(now)
  m.setDate(now.getDate() - dow + offset * 7)
  m.setHours(0, 0, 0, 0)
  return m
}
const sameOrAfter = (a: Date, b: Date) => a.getTime() >= b.getTime()
const dayLabel = (d: Date) => d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'short' })
const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })

function StoryPreview({ post, onClose }: { post: CalendarPost; onClose: () => void }) {
  const img = post.images.story || post.images.post || post.images.thumb
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 p-4" onClick={onClose}>
      <button className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white" onClick={onClose}><X className="h-5 w-5" /></button>
      {img ? (
        <img src={img} alt="" className="max-h-[80vh] max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
      ) : (
        <p className="text-white">Sin imagen</p>
      )}
      {img && (
        <a href={img} download target="_blank" rel="noreferrer" className="mt-4 flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-ink" onClick={(e) => e.stopPropagation()}>
          <Download className="h-4 w-4" /> Descargar imagen
        </a>
      )}
    </div>
  )
}

function PostCard({ post }: { post: CalendarPost }) {
  const [copy, setCopy] = useState(post.copy)
  const [copied, setCopied] = useState(false)
  const [preview, setPreview] = useState(false)
  const thumb = post.images.post || post.images.story || post.images.thumb

  async function copyContent() {
    const text = post.productUrl ? `${copy}\n${post.productUrl}` : copy
    try {
      await navigator.clipboard.writeText(text.trim())
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="card overflow-hidden">
      {thumb && <img src={thumb} alt="" className="h-36 w-full object-cover" />}
      <div className="space-y-2 p-3">
        <p className="text-[11px] font-semibold accent-text">{timeLabel(post.scheduledAt)} hs</p>
        {post.productName && <p className="truncate text-xs text-ink-muted">🔗 {post.productName}</p>}
        <textarea
          className="field-input min-h-[70px] resize-none text-sm"
          value={copy}
          onChange={(e) => setCopy(e.target.value)}
        />
        <div className="flex gap-2">
          <button className="btn-primary flex-1 py-2 text-xs" onClick={copyContent}>
            {copied ? <><Check className="h-3.5 w-3.5" /> ¡Copiado!</> : <><Copy className="h-3.5 w-3.5" /> Copiar contenido</>}
          </button>
          <button className="btn-secondary py-2 text-xs" onClick={() => setPreview(true)} disabled={!thumb}>
            <Eye className="h-3.5 w-3.5" /> Ver story
          </button>
        </div>
      </div>
      {preview && <StoryPreview post={post} onClose={() => setPreview(false)} />}
    </div>
  )
}

export function Calendar() {
  const [weekOffset, setWeekOffset] = useState(0)
  const { data, loading, error, reload } = useAsync(() => api.get<CalendarResponse>('/calendar'), [])

  const days = useMemo(() => {
    const monday = mondayOf(weekOffset)
    const posts = data?.posts ?? []
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(monday)
      day.setDate(monday.getDate() + i)
      const next = new Date(day)
      next.setDate(day.getDate() + 1)
      const dayPosts = posts
        .filter((p) => {
          const t = new Date(p.scheduledAt)
          return sameOrAfter(t, day) && !sameOrAfter(t, next)
        })
        .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
      return { day, posts: dayPosts }
    }).filter((d) => d.posts.length > 0)
  }, [data, weekOffset])

  const label = weekOffset === 0 ? 'Esta semana' : weekOffset < 0 ? `Hace ${-weekOffset} sem.` : `En ${weekOffset} sem.`

  return (
    <div className="flex flex-col">
      <div data-tour="calendar" className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-surface px-4 py-3">
        <h2 className="font-bold text-ink">Novedades</h2>
        <div className="flex items-center gap-2">
          <button className="btn-ghost px-2 py-1" onClick={() => setWeekOffset((w) => w - 1)} aria-label="Semana anterior"><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[6.5rem] text-center text-sm font-medium text-ink-soft">{label}</span>
          <button className="btn-ghost px-2 py-1" onClick={() => setWeekOffset((w) => w + 1)} aria-label="Semana siguiente"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      {loading ? (
        <LoadingScreen label="Cargando novedades…" />
      ) : error ? (
        <ErrorNote message={error.message} onRetry={reload} />
      ) : days.length === 0 ? (
        <EmptyState title="Sin contenido programado para esta semana." subtitle="Probá con otra semana o consultá con tu administrador." />
      ) : (
        <div className="space-y-5 p-4">
          {days.map(({ day, posts }) => (
            <section key={day.toISOString()}>
              <h3 className="mb-2 text-sm font-semibold capitalize text-ink">{dayLabel(day)}</h3>
              <div className="space-y-3">
                {posts.map((p) => (
                  <PostCard key={p.id} post={p} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
